class SegmentHandler {
  constructor() {
    this.storageKey = 'site_segment';
    this.customerData = window.shopifyCustomer || { isLoggedIn: false, isB2B: false };
    this.init();
    
    // Listen for browser back/forward navigation
    window.addEventListener('popstate', () => {
      this.handlePopState();
    });
  }

  handlePopState() {
    const storedSegment = sessionStorage.getItem(this.storageKey);
    const urlParams = new URLSearchParams(window.location.search);
    const urlSegment = urlParams.get('seg');

    // If we're on coffee-shrub page, ensure b2b segment is set
    if (window.location.pathname.includes('/pages/coffee-shrub') || window.location.pathname.includes('coffee-shrub')) {
      if (storedSegment !== 'b2b') {
        this.setSegment('b2b', false);
      }
      return;
    }

    // Sync sessionStorage to match the current URL's seg param
    if (urlSegment === 'b2b' || urlSegment === 'b2c') {
      if (storedSegment !== urlSegment) {
        this.setSegment(urlSegment, false);
      }
    } else if (storedSegment) {
      sessionStorage.removeItem(this.storageKey);
      sessionStorage.removeItem('segment_source');
      document.body.classList.remove('segment-b2b', 'segment-b2c');

      window.dispatchEvent(new CustomEvent('segmentChanged', { detail: { segment: null } }));

      const productInfoElements = document.querySelectorAll('product-info');
      productInfoElements.forEach(element => {
        if (typeof element.updateBuyButtonsState === 'function') {
          element.updateBuyButtonsState();
        }
      });
    }
  }

  init() {
    const urlParams = new URLSearchParams(window.location.search);
    let urlSegment = urlParams.get('seg');
    let needsUrlCleanup = false;
    
    if (urlSegment) {
      const originalSegment = urlSegment;
      urlSegment = urlSegment.split('?')[0].split('&')[0].trim();
      if (urlSegment !== 'b2b' && urlSegment !== 'b2c') {
        urlSegment = null;
      }
      if (urlSegment && originalSegment !== urlSegment) {
        needsUrlCleanup = true;
      }
    }
    
    const storedSegment = sessionStorage.getItem(this.storageKey);
    
    // Treat coffee-shrub page as b2b mode without adding URL parameter
    if ((window.location.pathname.includes('/pages/coffee-shrub') || window.location.pathname.includes('coffee-shrub')) && !urlSegment) {
      sessionStorage.setItem('segment_source', 'coffee-shrub');
      this.setSegment('b2b', true);
      return;
    }
    
    if (!this.customerData.isLoggedIn && storedSegment) {
      sessionStorage.removeItem(this.storageKey);  
      document.body.classList.remove('segment-b2b', 'segment-b2c');
      
      // Dispatch event to notify ProductCard elements to update
      window.dispatchEvent(new CustomEvent('segmentChanged', { detail: { segment: null } }));
      
      if (!urlSegment) {
        this.handleSegmentTriggers();
        return;
      }
    }
    
    if (this.customerData.isLoggedIn && this.customerData.isB2B && !urlSegment && !storedSegment && !window.location.pathname.startsWith('/products/')) {
      this.setSegment('b2c', true);
      return;
    }
        
    if (urlSegment === 'b2b') {
      sessionStorage.setItem('segment_source', 'url');
      this.setSegment('b2b', needsUrlCleanup);
    } else if (urlSegment === 'b2c') {
      if (this.customerData.isLoggedIn && this.customerData.isB2B) {
        this.setSegment('b2c', needsUrlCleanup);
      } else {
        sessionStorage.removeItem(this.storageKey);
        document.body.classList.remove('segment-b2b', 'segment-b2c');
      }
    } else {
      const currentStoredSegment = sessionStorage.getItem(this.storageKey);
      if (currentStoredSegment) {
        const segmentSource = sessionStorage.getItem('segment_source');
        if (segmentSource === 'coffee-shrub') {
          // Segment was set implicitly by coffee-shrub, not via URL — clear it
          sessionStorage.removeItem(this.storageKey);
          sessionStorage.removeItem('segment_source');
          document.body.classList.remove('segment-b2b', 'segment-b2c');
          window.dispatchEvent(new CustomEvent('segmentChanged', { detail: { segment: null } }));
        } else {
          // Segment was set via URL (e.g. login redirect stripped the param) — restore it
          const urlParams = new URLSearchParams(window.location.search);
          if (!urlParams.has('seg')) {
            urlParams.set('seg', currentStoredSegment);
            const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
            window.history.replaceState({}, '', newUrl);
          }
          if (currentStoredSegment === 'b2b') {
            document.body.classList.add('segment-b2b');
            document.body.classList.remove('segment-b2c');
          } else if (currentStoredSegment === 'b2c') {
            document.body.classList.add('segment-b2c');
            document.body.classList.remove('segment-b2b');
          }
          this.attachLinkHandlers();
        }
      }
    }
    this.handleSegmentTriggers();
  }

  setSegment(segment, updateUrl = false) {
    sessionStorage.setItem(this.storageKey, segment);
    
    if (updateUrl) {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set('seg', segment);
      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      window.history.replaceState({}, '', newUrl);
    }
    
    if (segment === 'b2b') {
      document.body.classList.add('segment-b2b');
      document.body.classList.remove('segment-b2c');
    } else if (segment === 'b2c') {
      document.body.classList.add('segment-b2c');
      document.body.classList.remove('segment-b2b');
    }
    
    this.attachLinkHandlers();
    this.handleSegmentTriggers();
  }

  attachLinkHandlers() {
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      if (!link) return;
      
      if (link.hasAttribute('data-segment-trigger')) return;
      
      // Ignore links inside cart-remove-button (handled by cart.js AJAX)
      if (link.closest('cart-remove-button')) return;
      
      const href = link.getAttribute('href');
      if (!href) return;
      
      if (this.isInternalLink(href)) {
        const storedSegment = sessionStorage.getItem(this.storageKey);
        
        const shouldAddParameter = this.shouldAddSegmentParameter(storedSegment);
        
        if (shouldAddParameter) {
          e.preventDefault();
          const url = new URL(href, window.location.origin);
          url.searchParams.set('seg', storedSegment);
          window.location.href = url.toString();
        }
      }
    });
    
    // Handle submit seaches to preserve segment parameter
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!form) return;
      
      this.addSegmentToForm(form);
    });
    
    document.addEventListener('click', (e) => {
      const button = e.target.closest('button');
      if (!button) return;
      
      const onclick = button.getAttribute('onclick');
      if (onclick && onclick.includes('.submit()')) {
        const form = button.closest('form') || button.closest('predictive-search')?.querySelector('form');
        if (form) {
          this.addSegmentToForm(form);
        }
      }
    }, true);
  }
  
  addSegmentToForm(form) {
    if (!form) return;
    
    const action = form.getAttribute('action');
    if (!action) return;
    
    if (this.isInternalLink(action)) {
      const storedSegment = sessionStorage.getItem(this.storageKey);
      const shouldAddParameter = this.shouldAddSegmentParameter(storedSegment);
      
      if (shouldAddParameter) {
        const existingSegInput = form.querySelector('input[name="seg"]');
        if (!existingSegInput) {
          const segInput = document.createElement('input');
          segInput.type = 'hidden';
          segInput.name = 'seg';
          segInput.value = storedSegment;
          form.appendChild(segInput);
        } else {
          existingSegInput.value = storedSegment;
        }
      }
    }
  }

  shouldAddSegmentParameter(segment) {
    // If customer is logged in and is B2B: add parameter for both b2b and b2c
    if (this.customerData.isLoggedIn && this.customerData.isB2B) {
      return true; // Add for both b2b and b2c
    }
    
    // If user is guest: only add parameter for b2b, not for b2c
    if (!this.customerData.isLoggedIn) {
      return segment === 'b2b'; // Only add for b2b
    }
    
    // If customer is logged in but NOT B2B (regular customer): only add b2b parameter
    if (this.customerData.isLoggedIn && !this.customerData.isB2B) {
      return segment === 'b2b'; // Only add for b2b, not for b2c
    }
    
    return false;
  }

  isInternalLink(href) {
    if (!href || 
        href.startsWith('#') || 
        href.startsWith('mailto:') || 
        href.startsWith('tel:') || 
        href.startsWith('javascript:') ||
        href.includes('//') && !href.includes(window.location.hostname)) {
      return false;
    }
    return true;
  }

  handleSegmentTriggers() {
    const triggers = document.querySelectorAll('[data-segment-trigger]');
    
    triggers.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        
        const segment = button.getAttribute('data-segment-trigger');
        const href = button.getAttribute('href') || '/';
        
        if (segment === 'b2b') {
          sessionStorage.setItem(this.storageKey, 'b2b');
          
          const url = new URL(href, window.location.origin);
          url.searchParams.set('seg', 'b2b');
          window.location.href = url.toString();
        } else if (segment === 'b2c') {
          const url = new URL(href, window.location.origin);
          
          if (this.customerData.isLoggedIn && this.customerData.isB2B) {
            sessionStorage.setItem(this.storageKey, 'b2c');
            url.searchParams.set('seg', 'b2c');
          } else {
            sessionStorage.removeItem(this.storageKey);
            url.searchParams.delete('seg');
          }
          
          window.location.href = url.toString();
        }
      });
    });
  }
}

// Store the instance globally to handle pageshow events
let segmentHandlerInstance = null;

function initSegmentHandler() {
  if (!segmentHandlerInstance) {
    segmentHandlerInstance = new SegmentHandler();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSegmentHandler);
} else {
  initSegmentHandler();
}

// Handle page restore from back-forward cache
window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    // Page was restored from bfcache, re-check segment
    if (segmentHandlerInstance) {
      segmentHandlerInstance.handlePopState();
    }
  }
});
